import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  GraduationCap, 
  Award,
  Users,
  Clock,
  CheckCircle,
  ArrowRight,
  Heart,
  Zap,
  Shield,
  BookOpen,
  Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import MarketingLayout from '../../components/marketing/MarketingLayout';
import { siteConfig } from '../../config/site';

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 }
  }
};

// Hero Section
function HeroSection() {
  return (
    <section className="pt-32 pb-16 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-3xl"
        >
          <motion.p variants={fadeInUp} className="text-blue-400 font-medium mb-4">
            Training Programs
          </motion.p>
          <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            First Aid &
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400"> Emergency Training</span>
          </motion.h1>
          <motion.p variants={fadeInUp} className="text-xl text-slate-400 leading-relaxed">
            Empower your team with life-saving skills. Our certified training programs prepare 
            individuals and organizations to respond confidently in emergencies.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

// Courses Section
function CoursesSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  const courses = [
    {
      icon: Heart,
      title: 'CPR & AED Certification',
      duration: '4 hours',
      description: 'Learn life-saving CPR techniques and how to use an Automated External Defibrillator.',
      topics: ['Adult, child, and infant CPR', 'AED operation', 'Choking response', 'Hands-only CPR'],
      price: '$75',
      popular: true
    },
    {
      icon: Shield,
      title: 'Basic First Aid',
      duration: '6 hours',
      description: 'Comprehensive first aid training for common injuries and medical emergencies.',
      topics: ['Wound care & bleeding control', 'Burns & fractures', 'Allergic reactions', 'Environmental emergencies'],
      price: '$95',
      popular: false
    },
    {
      icon: Zap,
      title: 'BLS for Healthcare Providers',
      duration: '4.5 hours',
      description: 'American Heart Association BLS certification for medical professionals.',
      topics: ['High-quality CPR', 'Team resuscitation', 'Bag-mask ventilation', 'AED use in clinical settings'],
      price: '$85',
      popular: false
    },
    {
      icon: BookOpen,
      title: 'Emergency Response Team',
      duration: '8 hours',
      description: 'Advanced training for workplace emergency response teams and safety officers.',
      topics: ['Scene assessment', 'Triage basics', 'Mass casualty incidents', 'Communication protocols'],
      price: '$150',
      popular: false
    },
    {
      icon: Users,
      title: 'Corporate Safety Training',
      duration: 'Custom',
      description: 'Tailored training programs for your organization&apos;s specific needs and environment.',
      topics: ['Workplace-specific hazards', 'Emergency action plans', 'Evacuation procedures', 'Team drills'],
      price: 'Contact Us',
      popular: false
    },
    {
      icon: GraduationCap,
      title: 'Instructor Certification',
      duration: '16 hours',
      description: 'Become a certified first aid instructor and train others in your organization.',
      topics: ['Adult learning principles', 'Course delivery methods', 'Skills assessment', 'Certification requirements'],
      price: '$350',
      popular: false
    }
  ];

  return (
    <section ref={ref} className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Our Training Courses
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              All courses are taught by certified instructors and include hands-on practice 
              with the latest training equipment.
            </p>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {courses.map((course, idx) => (
              <motion.div
                key={idx}
                variants={fadeInUp}
                whileHover={{ y: -8 }}
                className={`relative p-6 rounded-3xl bg-slate-900/50 border ${
                  course.popular ? 'border-blue-500/50' : 'border-slate-800'
                } hover:border-blue-500/30 transition-all`}
              >
                {course.popular && (
                  <div className="absolute -top-3 left-6">
                    <span className="px-3 py-1 rounded-full bg-blue-500 text-white text-xs font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-4">
                  <course.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{course.title}</h3>
                <div className="flex items-center gap-3 mb-3">
                  <span className="flex items-center gap-1 text-slate-400 text-sm">
                    <Clock className="w-4 h-4" />
                    {course.duration}
                  </span>
                  <span className="text-blue-400 font-semibold">{course.price}</span>
                </div>
                <p className="text-slate-400 text-sm mb-4">{course.description}</p>
                <ul className="space-y-2">
                  {course.topics.map((topic, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0" />
                      {topic}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// Why Train With Us Section
function WhyTrainSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  const benefits = [
    {
      icon: Award,
      title: 'Certified Instructors',
      description: 'All our instructors are AHA-certified with real-world emergency experience.'
    },
    {
      icon: Users,
      title: 'Small Class Sizes',
      description: 'Maximum 12 students per class ensures personalized attention and hands-on practice.'
    },
    {
      icon: Calendar,
      title: 'Flexible Scheduling',
      description: 'Weekend, evening, and on-site training options to fit your schedule.'
    },
    {
      icon: Shield,
      title: 'Recognized Certifications',
      description: 'American Heart Association and Red Cross certifications accepted nationwide.'
    }
  ];

  return (
    <section ref={ref} className="py-24 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
          className="grid lg:grid-cols-2 gap-12 items-center"
        >
          <motion.div variants={fadeInUp}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-6">
              <GraduationCap className="w-4 h-4" />
              <span>Why Train With Us</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Expert Training for Real Emergencies
            </h2>
            <p className="text-slate-400 text-lg leading-relaxed mb-8">
              Our training programs go beyond certification. We prepare you to respond confidently 
              when seconds count, using proven techniques and realistic scenarios.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">{benefit.title}</h4>
                    <p className="text-slate-400 text-sm">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="p-8 rounded-3xl bg-slate-950 border border-slate-800">
            <h3 className="text-2xl font-bold text-white mb-6">Schedule Your Training</h3>
            <p className="text-slate-400 mb-6">
              Contact us to discuss your training needs and get a customized program for your team.
            </p>
            <ul className="space-y-4 mb-8">
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle className="w-5 h-5 text-blue-400" />
                On-site training available
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle className="w-5 h-5 text-blue-400" />
                Group discounts for 10+ participants
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle className="w-5 h-5 text-blue-400" />
                All materials and equipment included
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle className="w-5 h-5 text-blue-400" />
                Certification cards issued same day
              </li>
            </ul>
            <Button 
              asChild
              size="lg" 
              className="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-full"
            >
              <Link to="/contact?type=training" data-testid="training-enroll-btn">
                Request Training
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// Corporate Section
function CorporateSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
          className="text-center space-y-8"
        >
          <motion.div variants={fadeInUp}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6">
              <Users className="w-4 h-4" />
              <span>Corporate Programs</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Train Your Entire Team
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              We offer customized corporate training programs that can be delivered on-site 
              at your location. Perfect for offices, warehouses, schools, and community organizations.
            </p>
          </motion.div>
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              asChild
              size="lg" 
              className="w-full sm:w-auto px-8 py-6 text-base bg-emerald-500 hover:bg-emerald-600 text-white rounded-full shadow-lg shadow-emerald-500/25"
            >
              <Link to="/contact?type=corporate-training" data-testid="corporate-training-btn">
                Get Corporate Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default function TrainingPage() {
  return (
    <MarketingLayout>
      <HeroSection />
      <CoursesSection />
      <WhyTrainSection />
      <CorporateSection />
    </MarketingLayout>
  );
}
